<template>
  <div class="screen-tab-status-footer">
    <div class="screen-tab-item-group">
      <div class="screen-tab-item">
        <div class="value">
          <screening-progress-breakdown
            class="total-progress-breakdown"
            :review="review"
            :full-text-review="fullTextReview"
            :height="14"
            hide-legend
          />
        </div>
        <div class="label">Total Progress</div>
      </div>
      <div class="screen-tab-item">
        <div class="value">
          {{ progressSummary.userVoteCountToday || 0 }}
        </div>
        <div class="label">Votes By You Today</div>
      </div>
      <div class="screen-tab-item">
        <div class="value">
          {{ progressSummary.userVoteCount || 0 }}
        </div>
        <div class="label">Total Votes By You</div>
      </div>
    </div>
    <div v-if="!fullTextReview" class="screen-tab-item-group">
      <div
        v-if="showVotesRequiredForTraining"
        class="screen-tab-item"
      >
        <div class="value">
          <div class="relevant-required-count">
            <v-icon
              color="success"
              size="14"
              class="material-icons-outlined"
            >
              thumb_up
            </v-icon>
            {{
              trainingStats.requiredRelevantVotesRemainingCount || 0
            }}
          </div>
          <div class="not-relevant-required-count">
            <v-icon
              color="error"
              size="14"
              class="material-icons-outlined"
            >
              thumb_down
            </v-icon>
            {{
              trainingStats.requiredNotRelevantVotesRemainingCount ||
              0
            }}
          </div>
        </div>
        <div class="label">Votes Required For Training</div>
      </div>
      <div v-if="showAwaitingTraining" class="screen-tab-item">
        <div class="value">Awaiting Training...</div>
        <div class="label">AI Status</div>
      </div>
      <div v-if="showVotesToTrain" class="screen-tab-item">
        <div class="value">
          {{ trainingStats.votesUntilTriggerTrainingCount || 0 }}
        </div>
        <div class="label">Votes To Train</div>
      </div>
      <div v-if="showIsTrainingText" class="screen-tab-item">
        <div class="value">Training...</div>
        <div class="label">AI Status</div>
      </div>
      <div v-if="showRemainingRelevant" class="screen-tab-item">
        <div class="value">~ {{ remainingRelevantText }}</div>
        <div class="label">Relevant Remaining</div>
      </div>
      <div v-if="showNotRelevantStreak" class="screen-tab-item">
        <div class="value">{{ notRelevantStreak }}</div>
        <div class="label">Not Relevant Streak</div>
      </div>
      <stop-screening-button
        v-if="showStopScreeningButton"
        :current-researcher="currentResearcher"
      />
      <stopping-criteria-help-button
        v-if="showStoppingCriteriaInfoButton"
      />
    </div>
  </div>
</template>

<script>
import ScreeningProgressBreakdown from '@/components/ReviewSummary/ScreeningProgressBreakdown';
import StoppingCriteriaHelpButton from './StoppingCriteriaHelpButton';
import StopScreeningButton from '../Common/StopScreeningButton';
import { Review, Researcher } from '@/models';
import { Mode } from '@/enums';

const TrainingState = Object.freeze({
  TRAINING_NEEDS_MORE_DATA: 'TRAINING_NEEDS_MORE_DATA',
  TRAINING_IDLE: 'TRAINING_IDLE',
  TRAINING_IN_PROGRESS: 'TRAINING_IN_PROGRESS',
});

const StoppingCriteriaState = Object.freeze({
  RELEVANT_REMAINING: 'RELEVANT_REMAINING',
  NOT_RELEVANT_STREAK_REMAINING: 'NOT_RELEVANT_STREAK_REMAINING',
  STOPPING_CRITERIA_MET: 'STOPPING_CRITERIA_MET',
});

export default {
  name: 'ScreenTabStatusFooter',
  components: {
    ScreeningProgressBreakdown,
    StoppingCriteriaHelpButton,
    StopScreeningButton,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    fullTextReview: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    showVotesRequiredForTraining() {
      return (
        this.trainingState === TrainingState.TRAINING_NEEDS_MORE_DATA
      );
    },
    showVotesToTrain() {
      return (
        !this.showAwaitingTraining &&
        this.trainingState === TrainingState.TRAINING_IDLE
      );
    },
    showAwaitingTraining() {
      return (
        this.trainingStats.votesUntilTriggerTrainingCount < 1 &&
        this.trainingState === TrainingState.TRAINING_IDLE
      );
    },
    showIsTrainingText() {
      return (
        this.trainingState === TrainingState.TRAINING_IN_PROGRESS
      );
    },
    showRemainingRelevant() {
      if (
        this.trainingState === TrainingState.TRAINING_NEEDS_MORE_DATA
      ) {
        return false;
      }
      return [
        StoppingCriteriaState.RELEVANT_REMAINING,
        StoppingCriteriaState.STOPPING_CRITERIA_MET,
      ].includes(this.stoppingCriteriaState);
    },
    showNotRelevantStreak() {
      return [
        StoppingCriteriaState.NOT_RELEVANT_STREAK_REMAINING,
        StoppingCriteriaState.STOPPING_CRITERIA_MET,
      ].includes(this.stoppingCriteriaState);
    },
    showStopScreeningButton() {
      return (
        this.stoppingCriteriaState ===
        StoppingCriteriaState.STOPPING_CRITERIA_MET
      );
    },
    showStoppingCriteriaInfoButton() {
      return (
        this.stoppingCriteriaState !==
        StoppingCriteriaState.STOPPING_CRITERIA_MET
      );
    },
    remainingRelevantText() {
      const totalRemaining = this.remainingRelevantAnalysis?.total;
      if (!totalRemaining) return 0;

      const upperRangeCount = totalRemaining.upperRangeCount;
      const lowerRangeCount = totalRemaining.lowerRangeCount;
      if (upperRangeCount === lowerRangeCount) {
        return upperRangeCount;
      }
      return `${upperRangeCount} - ${lowerRangeCount}`;
    },
    trainingState() {
      const trainingModes = [Mode.TRAIN, Mode.TRAIN_AND_PREDICT];
      if (trainingModes.includes(this.review.activeJob?.mode)) {
        return TrainingState.TRAINING_IN_PROGRESS;
      }
      if (
        this.trainingStats.requiredRelevantVotesRemainingCount > 0 ||
        this.trainingStats.requiredNotRelevantVotesRemainingCount > 0
      ) {
        return TrainingState.TRAINING_NEEDS_MORE_DATA;
      }

      return TrainingState.TRAINING_IDLE;
    },
    stoppingCriteriaState() {
      if (this.review.shouldStopScreening()) {
        return StoppingCriteriaState.STOPPING_CRITERIA_MET;
      }
      if (this.review.anyRemainingRelevant) {
        return StoppingCriteriaState.RELEVANT_REMAINING;
      }
      return StoppingCriteriaState.NOT_RELEVANT_STREAK_REMAINING;
    },
    progressSummary() {
      const progressSummary = this.fullTextReview
        ? this.review.fullTextReviewScreeningProgressSummary
        : this.review.screeningProgressSummary;
      return progressSummary ?? {};
    },
    trainingStats() {
      return this.review.trainingStats ?? {};
    },
    remainingRelevantAnalysis() {
      return this.review.remainingRelevantAnalysis ?? {};
    },
    notRelevantStreak() {
      return this.review.negativeStreak() ?? 0;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/main.sass';

.screen-tab-status-footer {
  @extend .d-flex,
    .justify-space-between,
    .align-center,
    .px-6,
    .py-4;

  background-color: map-get($grey, 'lighten-5');
  border-top: 1px solid map-get($grey, 'lighten-2');

  .screen-tab-item-group {
    @extend .d-flex, .align-center;
    gap: 24px;
  }

  .screen-tab-item {
    display: grid;
    grid-template-rows: 1fr 1fr;
    gap: 4px;

    @extend .grey--text, .text--darken-2;

    .value {
      @extend .d-flex;
      gap: 8px;

      @extend .font-weight-bold;
      font-size: 14px;
    }
    .label {
      @extend .font-weight-light;
      font-size: 10px;
    }
  }

  .total-progress-breakdown {
    width: 172px;
  }
}
</style>
